import type { TenantBundle } from "@crosure/tenant-runtime";

/**
 * Brand surface for Green Shield Insurance Brokers — logo, favicon, title,
 * contact details rendered as chrome by the skeleton.
 *
 * Assets are referenced by path; the skeleton's Next.js build resolves
 * them through the sync overlay (src/assets → next/public).
 *
 * The logo is the real mark (1321×297 PNG, as served by greenshieldbrokers.com)
 * rather than the scaffold's placeholder SVG, which was a blue rectangle with
 * the company name set in it as text. There are two files because the design
 * needs both: `logo.png` is the colour mark for light chrome, `logo-white.png`
 * the reversed mark for the navy footer. The white one is reached through
 * `data/translations/common.json` (`logoWhite`), which is where the skeleton
 * looks for it.
 *
 * No `supportPhone`: the live site publishes an email and a postal address and
 * no telephone number at all — its own schema.org block carries
 * `"telephone": ""`. Inventing one here would put a number on the page that
 * nobody answers.
 */
export const brand: TenantBundle["brand"] = {
  logoSrc: "/assets/green-shield-insurance-brokers/logo.png",
  faviconHref: "/assets/green-shield-insurance-brokers/favicon.png",
  appTitle: "Green Shield Insurance Brokers",
  supportEmail: "Info@greenshieldbrokers.com",
  // The licensed entity, as it appears on the footer of the live site — not
  // the trading name. This is the string that ends up in legal small print.
  legalEntityName: "Green Shield Insurance Brokers Ltd",
  tagline:
    "Insurance advisory and placement supported by expertise, governance and clarity.",
  registeredAddress:
    "NIB Tower, 6th Floor, Office No. 610, P.O. Box 8878, Jeddah 23423, Sari Street, Jeddah, Saudi Arabia",
  // Licence and CR number verbatim from the live About page. These are
  // regulatory identifiers — do not reformat, reorder or "tidy" the Arabic
  // licence suffix; it is part of the number the Insurance Authority issued.
  footerLegalText:
    "Green Shield Insurance Brokers Ltd is licensed by the Saudi Insurance Authority under licence No. 20083 و س ط/ش/7. Commercial Registration 4030170929. Green Shield is supervised and regulated by the Insurance Authority.",
  canonicalUrl: "https://greenshieldbrokers.com",
  // The live site publishes no social profiles, so `socialLinks` is omitted
  // rather than pointed at accounts that may not exist.
};
